<aside
  id="layout-sidebar"
  class="sidebar"
  [class.is-collapsed]="collapsed()"
  [class.is-mobile-open]="mobileOpen()"
  aria-label="Workspace navigation"
>
  @if (collapsed()) {
    <button
      class="sidebar__expand-overlay"
      type="button"
      aria-label="Expand sidebar"
      aria-controls="layout-sidebar-content"
      (click)="commandRequested.emit({ kind: 'expand-sidebar' })"
    ></button>
  }

  <div class="sidebar__header">
    <a
      class="sidebar__logo"
      href="/"
      aria-label="Automatick home"
      (click)="requestHome($event)"
    >
      <span class="sidebar__logo-mark" aria-hidden="true">
        <i sch-icon="dashboard-circle"></i>
      </span>
      <span class="sidebar__logo-text">Automatick</span>
    </a>

    @if (!collapsed()) {
      <button
        class="sidebar__icon-button sidebar__collapse-button"
        type="button"
        aria-label="Collapse sidebar"
        aria-controls="layout-sidebar-content"
        aria-expanded="true"
        (click)="commandRequested.emit({ kind: 'toggle-sidebar' })"
      >
        <i sch-icon="arrow-back"></i>
      </button>
    }

    @if (mobileOpen()) {
      <button
        #mobileCloseButton
        class="sidebar__icon-button sidebar__mobile-close"
        type="button"
        aria-label="Close navigation"
        aria-controls="layout-sidebar"
        aria-expanded="true"
        (click)="commandRequested.emit({ kind: 'close-mobile-navigation' })"
      >
        <i sch-icon="close"></i>
      </button>
    }
  </div>

  <div id="layout-sidebar-content" class="sidebar__content">
    <nav class="sidebar__navigation" aria-label="Primary">
      @for (item of model().navigation; track item.route) {
        <a
          class="sidebar__navigation-link"
          [class.is-active]="item.isActive"
          [attr.aria-current]="item.isActive ? 'page' : null"
          [href]="item.route"
          [title]="collapsed() ? item.label : null"
          (click)="requestNavigation($event, item)"
        >
          @if (item.icon) {
            <i
              class="sidebar__navigation-icon"
              [sch-icon]="item.icon"
              aria-hidden="true"
            ></i>
          }
          <span class="sidebar__navigation-label">{{ item.label }}</span>
        </a>
      }
    </nav>

    <section class="sidebar__activity" aria-label="Projects and conversations">
      <div class="sidebar__section-heading">
        <span>Projects</span>
        <button
          class="sidebar__icon-button sidebar__heading-action"
          type="button"
          aria-label="New project"
          [disabled]="!model().isChatReady"
          (click)="commandRequested.emit({ kind: 'create-project' })"
        >
          <i sch-icon="add-circle"></i>
        </button>
      </div>

      @if (model().isChatReady) {
        <div class="sidebar__project-list">
          @for (project of model().projects; track project.projectId) {
            <button
              class="sidebar__project"
              type="button"
              [class.is-active]="project.isSelected"
              [attr.aria-pressed]="project.isSelected"
              (click)="requestProject(project)"
            >
              <i
                class="sidebar__project-icon"
                sch-icon="folder"
                aria-hidden="true"
              ></i>
              <span class="sidebar__project-label">{{ project.label }}</span>
            </button>
          }

          @if (model().projects.length === 0) {
            <button
              class="sidebar__project sidebar__project--new"
              type="button"
              (click)="commandRequested.emit({ kind: 'create-project' })"
            >
              <i
                class="sidebar__project-icon"
                sch-icon="add-circle"
                aria-hidden="true"
              ></i>
              <span class="sidebar__project-label">New project</span>
            </button>
          }
        </div>
      }

      <div class="sidebar__section-heading">
        <span>Conversations</span>
        <span class="sidebar__heading-actions">
          <button
            class="sidebar__icon-button sidebar__heading-action"
            type="button"
            aria-label="Search conversations"
            [disabled]="!model().searchSessionId"
            (click)="requestSearch()"
          >
            <i sch-icon="search"></i>
          </button>
          @if (model().showNewConversation) {
            <button
              class="sidebar__icon-button sidebar__heading-action"
              type="button"
              aria-label="New conversation"
              [disabled]="!model().isChatReady"
              (click)="commandRequested.emit({ kind: 'create-conversation' })"
            >
              <i sch-icon="add-circle"></i>
            </button>
          }
        </span>
      </div>

      @if (model().isChatReady && model().conversations.length > 0) {
        <div class="sidebar__conversation-list">
          @for (
            conversation of model().conversations;
            track conversation.sessionId
          ) {
            <div
              class="sidebar__conversation-row"
              [class.is-active]="conversation.isActive"
            >
              <button
                class="sidebar__conversation"
                type="button"
                [class.is-active]="conversation.isActive"
                [attr.aria-current]="conversation.isActive ? 'location' : null"
                (click)="requestConversation(conversation)"
              >
                <i
                  class="sidebar__conversation-icon"
                  sch-icon="messaging"
                  aria-hidden="true"
                ></i>
                <span class="sidebar__conversation-info">
                  <span class="sidebar__conversation-title">
                    {{ conversation.title }}
                  </span>
                  @if (conversation.context) {
                    <span class="sidebar__conversation-context">
                      {{ conversation.context }}
                    </span>
                  }
                </span>
              </button>

              <button
                class="sidebar__conversation-menu-trigger"
                type="button"
                [attr.aria-label]="'Options for ' + conversation.title"
                [sch-menu-trigger-for]="conversationMenu"
              >
                <i sch-icon="more-vertical-fill"></i>
              </button>
              <ng-template #conversationMenu>
                <div sch-menu>
                  <button
                    sch-menu-item
                    type="button"
                    (click)="requestRename(conversation)"
                  >
                    <i sch-icon="edit"></i>
                    <span>Rename</span>
                  </button>
                  <button
                    sch-menu-item
                    type="button"
                    (click)="requestArchive(conversation)"
                  >
                    <i sch-icon="folder"></i>
                    <span>Archive</span>
                  </button>
                  <button
                    sch-menu-item
                    type="button"
                    (click)="requestDelete(conversation)"
                  >
                    <i sch-icon="delete"></i>
                    <span>Delete</span>
                  </button>
                </div>
              </ng-template>
            </div>
          }
        </div>
      }

      @if (model().isChatReady && model().conversations.length === 0) {
        <div class="sidebar__empty-state">
          <span class="sidebar__empty-icon" aria-hidden="true">
            <i sch-icon="messaging"></i>
          </span>
          <p>No conversations yet</p>
          <button
            class="sidebar__start-conversation"
            type="button"
            (click)="commandRequested.emit({ kind: 'create-conversation' })"
          >
            Start a conversation
          </button>
        </div>
      }

      @if (model().isChatLoading) {
        <div class="sidebar__loading" role="status">
          <i sch-icon="auto-renew" class="sidebar__spin"></i>
          <span>Loading...</span>
        </div>
      }
    </section>
  </div>

  <ng-content select="app-layout-profile-menu"></ng-content>
</aside>
